// Preserve delegated matching for existing data attributes, including nested click targets.
// eslint-disable-next-line no-restricted-imports
import {on, off} from 'delegated-events'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {debugHydroPayload} from '@github-ui/console-debug'
// Preserve observation of existing attributes and dynamically inserted markup.
// eslint-disable-next-line no-restricted-imports
import {observe} from '@github-ui/selector-observer'
// Preserve per-element error reporting without aborting the remaining impressions.
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'

const GENERIC_CLICK_EVENT_NAME = 'analytics.click'
const GENERIC_VISIBLE_EVENT_NAME = 'analytics.visible'
const ANALYTICS_VISIBLE_ATTR = 'data-analytics-visible'
const VISIBLE_SELECTORS = `[${ANALYTICS_VISIBLE_ATTR}]`
// Share initialization across bundles without adding attributes to React's document.
const initialization = Symbol.for('github.analytics-behaviors.events')
type AnalyticsDocument = Document & {[initialization]?: () => void}
type LabelFields = Record<string, string>

function isMarketingPage(): boolean {
  return !!ssrSafeDocument?.head?.querySelector<HTMLMetaElement>('meta[name="is_logged_out_page"]')?.content
}

function sendClickEvent(event: Event & {currentTarget: Element}) {
  // Marketing pages use marketing/analytics-events.ts instead.
  if (isMarketingPage()) return

  const analyticsEvent = event.currentTarget.getAttribute('data-analytics-event')
  if (!analyticsEvent) return

  const analyticsEventAttributes = JSON.parse(analyticsEvent)
  debugHydroPayload('hydro-debug.click', `{"event_type": "${GENERIC_CLICK_EVENT_NAME}", "payload": ${analyticsEvent}}`)
  sendEvent(GENERIC_CLICK_EVENT_NAME, analyticsEventAttributes)
}

function isTag(element: Element, tagName: 'a'): element is HTMLAnchorElement
function isTag(element: Element, tagName: 'button'): element is HTMLButtonElement
function isTag(element: Element, tagName: 'a' | 'button'): boolean {
  return element.tagName.toLowerCase() === tagName
}

function parseLabels(labelString: string) {
  if (!labelString) return {}

  const labelFields: LabelFields = {}
  const labels = labelString.split(';').map(label => label.trim())
  for (const label of labels) {
    const [key, val] = label.split(':')
    if (key) labelFields[key.trim()] = val?.trim() || key.trim()
  }
  return labelFields
}

function parseAnalyticsAttr(dataAnalyticsClick: string | null) {
  if (!dataAnalyticsClick) return {}

  const analyticsEventAttributes = JSON.parse(dataAnalyticsClick)
  const {label} = analyticsEventAttributes
  return {
    ...parseLabels(label),
    ...analyticsEventAttributes,
  }
}

function getLinkAttributes(linkEl: HTMLAnchorElement) {
  return {
    text: linkEl.textContent || linkEl.getAttribute('aria-label') || '',
    target: linkEl.href,
  }
}

function getButtonAttributes(buttonEl: HTMLButtonElement) {
  const form = buttonEl.closest('form')
  return {
    text: buttonEl.textContent || buttonEl.getAttribute('aria-label') || '',
    role: buttonEl.getAttribute('type') || buttonEl.getAttribute('role') || 'button',
    ...(buttonEl.value && {value: buttonEl.value}),
    ...(form && {formAction: form.getAttribute('action') || ''}),
  }
}

function getVisibleContext(currentTarget: Element) {
  return {
    ...(isTag(currentTarget, 'a') && getLinkAttributes(currentTarget)),
    ...(isTag(currentTarget, 'button') && getButtonAttributes(currentTarget)),
    ...parseAnalyticsAttr(currentTarget.getAttribute(ANALYTICS_VISIBLE_ATTR)),
  }
}

export function setupAnalyticsEvents(): (() => void) | undefined {
  const document: AnalyticsDocument | undefined = ssrSafeDocument
  if (!document) return
  if (document[initialization]) return document[initialization]

  const defaultObserver = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          try {
            sendEvent(GENERIC_VISIBLE_EVENT_NAME, getVisibleContext(entry.target))
            defaultObserver.unobserve(entry.target)
          } catch (e) {
            reportError(e)
          }
        }
      }
    },
    {rootMargin: '0% 0% -30% 0%', threshold: 0},
  )

  // Registration is explicit so importing this module during SSR has no DOM effects.
  // eslint-disable-next-line delegated-events/global-on
  on('click', '[data-analytics-event]', sendClickEvent)
  const observation = observe(VISIBLE_SELECTORS, element => {
    defaultObserver.observe(element)
  })

  const cleanup = () => {
    off('click', '[data-analytics-event]', sendClickEvent)
    observation.abort()
    defaultObserver.disconnect()
    delete document[initialization]
  }
  document[initialization] = cleanup
  return cleanup
}
